<template>
  <div class="modal fade" tabindex="-1" id="modal_prof_tax_info" ref="modal_prof_tax_info">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            Informações Fiscais<br>
            <span class="text-muted">{{ professional.prof_name }}</span>
          </h5>
          <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Fechar">
            <i class="bi bi-x-lg"></i>
          </div>
        </div>
        <div class="modal-body">
          <div class="container">
            <form v-if="!loading" @submit.prevent="submitForm">
              <!-- Alerta de bloqueio de edição -->
              <div v-if="!canBeEdited && editBlockedReason" class="alert alert-warning d-flex align-items-center mb-4">
                <i class="bi bi-lock-fill me-2 fs-4"></i>
                <div>
                  <strong>Edição bloqueada:</strong> {{ editBlockedReason }}
                </div>
              </div>

              <div class="row">
                <!-- Mês de Referência -->
                <div class="col-md-6 mb-4">
                  <label for="reference_month" class="form-label required">Mês de Referência</label>
                  <Calendar
                    v-model="referenceMonthDate"
                    view="month"
                    dateFormat="mm/yy"
                    :showIcon="true"
                    :manualInput="false"
                    class="w-100"
                    inputClass="form-control form-control-solid"
                    @date-select="onMonthChange"
                    placeholder="Selecione o mês"
                  />
                  <small class="text-danger" v-if="formErrors.reference_month">
                    {{ formErrors.reference_month.join('\n') }}
                  </small>
                </div>

                <!-- Quantidade de Dependentes -->
                <div class="col-md-6 mb-4">
                  <label for="dependents_count" class="form-label">Quantidade de Dependentes</label>
                  <input
                    type="number"
                    class="form-control form-control-solid"
                    :class="{ 'bg-light': !canBeEdited }"
                    id="dependents_count"
                    v-model.number="form.dependents_count"
                    min="0"
                    max="99"
                    placeholder="0"
                    :disabled="!canBeEdited"
                  />
                  <small class="text-danger" v-if="formErrors.dependents_count">
                    {{ formErrors.dependents_count.join('\n') }}
                  </small>
                </div>

                <!-- Valor de Pensão Alimentícia -->
                <div class="col-md-6 mb-4">
                  <label for="alimony_value" class="form-label">Valor de Pensão Alimentícia</label>
                  <money3
                    class="form-control form-control-solid"
                    :class="{ 'bg-light': !canBeEdited }"
                    id="alimony_value"
                    v-model="form.alimony_value"
                    v-bind="moneyConfigComputed"
                  ></money3>
                  <small class="text-danger" v-if="formErrors.alimony_value">
                    {{ formErrors.alimony_value.join('\n') }}
                  </small>
                </div>

                <!-- Retenção Externa de INSS -->
                <div class="col-md-6 mb-4">
                  <label for="external_inss_retention" class="form-label">
                    INSS já Retido em Outras Fontes
                    <i class="bi bi-info-circle" title="Valor de INSS já retido em outras fontes pagadoras no mês"></i>
                  </label>
                  <money3
                    class="form-control form-control-solid"
                    :class="{ 'bg-light': !canBeEdited }"
                    id="external_inss_retention"
                    v-model="form.external_inss_retention"
                    v-bind="moneyConfigComputed"
                  ></money3>
                  <small class="text-danger" v-if="formErrors.external_inss_retention">
                    {{ formErrors.external_inss_retention.join('\n') }}
                  </small>
                </div>

                <!-- Retenção Externa de IRRF -->
                <div class="col-md-6 mb-4">
                  <label for="external_irrf_retention" class="form-label">
                    IRRF já Retido em Outras Fontes
                    <i class="bi bi-info-circle" title="Valor de IRRF já retido em outras fontes pagadoras no mês"></i>
                  </label>
                  <money3
                    class="form-control form-control-solid"
                    :class="{ 'bg-light': !canBeEdited }"
                    id="external_irrf_retention"
                    v-model="form.external_irrf_retention"
                    v-bind="moneyConfigComputed"
                  ></money3>
                  <small class="text-danger" v-if="formErrors.external_irrf_retention">
                    {{ formErrors.external_irrf_retention.join('\n') }}
                  </small>
                </div>

                <!-- Observações -->
                <div class="col-md-12 mb-4">
                  <label for="notes" class="form-label">Observações</label>
                  <textarea
                    class="form-control form-control-solid"
                    :class="{ 'bg-light': !canBeEdited }"
                    id="notes"
                    v-model="form.notes"
                    rows="3"
                    placeholder="Observações adicionais..."
                    :disabled="!canBeEdited"
                  ></textarea>
                  <small class="text-danger" v-if="formErrors.notes">
                    {{ formErrors.notes.join('\n') }}
                  </small>
                </div>
              </div>

              <!-- Info box quando não existe registro -->
              <div v-if="!taxInfoId && !loading && canBeEdited" class="alert alert-info d-flex align-items-center mb-4">
                <i class="bi bi-info-circle me-2 fs-4"></i>
                <div>
                  Não existe registro de informações fiscais para o mês selecionado. 
                  Preencha os campos e salve para criar um novo registro.
                </div>
              </div>

              <div class="modal-footer px-0 pb-0">
                <button type="button" class="btn btn-light" data-bs-dismiss="modal">Fechar</button>
                <button 
                  v-if="canBeEdited"
                  type="submit" 
                  class="btn btn-primary" 
                  :disabled="saving"
                >
                  <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ saving ? 'Salvando...' : 'Salvar' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../helpers/http";
import Alert from "../../helpers/alert";
import Calendar from "primevue/calendar";
import { Money3 } from "v-money3";

export default {
  name: "ModalProfTaxInfo",
  components: {
    Calendar,
    Money3,
  },
  props: {
    professional: {
      type: Object,
      required: true,
    },
  },
  emits: ['saved'],
  data() {
    return {
      loading: false,
      saving: false,
      taxInfoId: null,
      referenceMonthDate: null,
      canBeEdited: true,
      editBlockedReason: null,
      form: {
        dependents_count: 0,
        alimony_value: 0,
        external_inss_retention: 0,
        external_irrf_retention: 0,
        notes: '',
      },
      formErrors: {},
      moneyConfig: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2,
        masked: false,
        disableNegative: true,
        disabled: false,
        min: 0,
        max: null,
        allowBlank: false,
        minimumNumberOfCharacters: 0,
      },
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    referenceMonth() {
      if (!this.referenceMonthDate) return null;
      const date = new Date(this.referenceMonthDate);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}-01`;
    },
    moneyConfigComputed() {
      return {
        ...this.moneyConfig,
        disabled: !this.canBeEdited,
      };
    },
  },
  mounted() {
    const modalElement = document.getElementById('modal_prof_tax_info');
    modalElement.addEventListener('show.bs.modal', () => {
      // Iniciar loading antes do modal abrir para evitar flash
      this.loading = true;
    });
    modalElement.addEventListener('shown.bs.modal', () => {
      this.initializeMonth();
      this.loadTaxInfo();
    });
    modalElement.addEventListener('hidden.bs.modal', () => {
      this.resetForm();
    });
  },
  methods: {
    initializeMonth() {
      // Inicializar com o mês atual
      const now = new Date();
      this.referenceMonthDate = new Date(now.getFullYear(), now.getMonth(), 1);
    },
    resetForm() {
      this.taxInfoId = null;
      this.referenceMonthDate = null;
      this.canBeEdited = true;
      this.editBlockedReason = null;
      this.form = {
        dependents_count: 0,
        alimony_value: 0,
        external_inss_retention: 0,
        external_irrf_retention: 0,
        notes: '',
      };
      this.formErrors = {};
    },
    onMonthChange() {
      this.loadTaxInfo();
    },
    async loadTaxInfo() {
      if (!this.place?.id_place || !this.professional?.id_prof || !this.referenceMonth) {
        return;
      }

      this.loading = true;
      this.formErrors = {};

      try {
        const response = await http.get(
          `/places/${this.place.id_place}/professionals/${this.professional.id_prof}/tax-info`,
          { params: { reference_month: this.referenceMonth } }
        );

        // Verificar se pode ser editado
        this.canBeEdited = response.data?.can_be_edited !== false;
        this.editBlockedReason = response.data?.edit_blocked_reason || null;

        if (response.data && response.data.id) {
          // Registro encontrado
          this.taxInfoId = response.data.id;
          this.form = {
            dependents_count: response.data.dependents_count || 0,
            alimony_value: response.data.alimony_value || 0,
            external_inss_retention: response.data.external_inss_retention || 0,
            external_irrf_retention: response.data.external_irrf_retention || 0,
            notes: response.data.notes || '',
          };
        } else {
          // Nenhum registro encontrado
          this.taxInfoId = null;
          this.form = {
            dependents_count: 0,
            alimony_value: 0,
            external_inss_retention: 0,
            external_irrf_retention: 0,
            notes: '',
          };
        }
      } catch (error) {
        // 404 ou outros erros - considerar como registro não existente
        if (error.response?.status !== 404) {
          console.error('Erro ao carregar informações fiscais:', error);
          Alert.toastError('Erro ao carregar informações fiscais.');
        }
        this.taxInfoId = null;
        this.canBeEdited = true;
        this.editBlockedReason = null;
        this.form = {
          dependents_count: 0,
          alimony_value: 0,
          external_inss_retention: 0,
          external_irrf_retention: 0,
          notes: '',
        };
      } finally {
        this.loading = false;
      }
    },
    validateForm() {
      this.formErrors = {};

      if (!this.referenceMonth) {
        this.formErrors.reference_month = ['O mês de referência é obrigatório.'];
      }

      if (this.form.dependents_count < 0 || this.form.dependents_count > 99) {
        this.formErrors.dependents_count = ['A quantidade de dependentes deve estar entre 0 e 99.'];
      }

      if (this.form.alimony_value < 0) {
        this.formErrors.alimony_value = ['O valor de pensão alimentícia não pode ser negativo.'];
      }

      if (this.form.external_inss_retention < 0) {
        this.formErrors.external_inss_retention = ['O valor de retenção de INSS não pode ser negativo.'];
      }

      if (this.form.external_irrf_retention < 0) {
        this.formErrors.external_irrf_retention = ['O valor de retenção de IRRF não pode ser negativo.'];
      }

      return Object.keys(this.formErrors).length === 0;
    },
    async submitForm() {
      // Verificar se a edição está bloqueada
      if (!this.canBeEdited) {
        Alert.toastError(this.editBlockedReason || 'Edição não permitida para este período.');
        return;
      }

      if (!this.validateForm()) {
        Alert.toastWarning('Por favor, corrija os erros no formulário.');
        return;
      }

      this.saving = true;

      const payload = {
        reference_month: this.referenceMonth,
        dependents_count: this.form.dependents_count,
        alimony_value: this.form.alimony_value,
        external_inss_retention: this.form.external_inss_retention,
        external_irrf_retention: this.form.external_irrf_retention,
        notes: this.form.notes || null,
      };

      try {
        if (this.taxInfoId) {
          // Atualizar registro existente
          await http.put(
            `/places/${this.place.id_place}/professionals/${this.professional.id_prof}/tax-info/${this.taxInfoId}`,
            payload
          );
          Alert.toastSuccess('Informações fiscais atualizadas com sucesso.');
        } else {
          // Criar novo registro
          const response = await http.post(
            `/places/${this.place.id_place}/professionals/${this.professional.id_prof}/tax-info`,
            payload
          );
          this.taxInfoId = response.data.id;
          Alert.toastSuccess('Informações fiscais cadastradas com sucesso.');
        }

        this.$emit('saved');
        this.formErrors = {};
      } catch (error) {
        console.error('Erro ao salvar informações fiscais:', error);
        
        // Tratamento específico para erro de lote fechado (HTTP 422)
        if (error.response?.status === 422 && error.response?.data?.error === 'batch_already_closed') {
          this.canBeEdited = false;
          this.editBlockedReason = error.response.data.message;
          Alert.toastError(error.response.data.message);
          return;
        }

        if (error.response?.data?.errors) {
          this.formErrors = error.response.data.errors;
        }
        
        const message = error.response?.data?.message || 'Erro ao salvar informações fiscais.';
        Alert.toastError(message);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.p-calendar {
  width: 100%;
}

:deep(.p-calendar .p-inputtext) {
  width: 100%;
}

:deep(.p-calendar-w-btn) {
  display: flex;
}

:deep(.p-calendar-w-btn .p-inputtext) {
  flex: 1;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

:deep(.p-datepicker-trigger) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>
